import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  Stack,
  LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useCustomTranslation } from "../../hooks/useCustomTranslation";
import { useApiClientMutation, useApiClientQuery } from "../../hooks/useApiClient";
import api_endpoints from "../../api";
import useFundraiserSession from "../../hooks/useFundraiserSession";
import LanguageSwitcher from "../Login/LanguageSwitcher";
import {
  EMAIL_REGEX,
  normalizeDonorEmail,
  translateFundraiserError,
  cloverSdkUrl,
  forteJsUrl,
  loadScript,
  FriendlyButton,
  calculateSurcharge,
  calculateTotal,
  buildPaymentPayload,
  createPaymentIntentId,
} from "./shared";

// Import modular components
import {
  EmailVerification,
  DonorRegistration,
  DonorProfile,
  PaymentMethodsList,
  CardPaymentForm,
  BankPaymentForm,
  PromiseForm,
  PaymentPreview,
  DonationSuccess,
  ExistingPromises,
  ExistingPromisePayment,
} from "./components";

/**
 * DesktopForm - Main orchestrating component for donor fundraiser flow
 * 
 * Business Logic:
 * - Registration is MANDATORY before making a donation
 * - A "promise" is the donation commitment (amount, frequency, campaign)
 * - Promise can be created without payment (promise-only flow)
 * - Payment REQUIRES an existing promise
 * - Donor profile stores saved payment methods
 * 
 * Flow:
 * 1. Email Verification (OTP)
 * 2. Registration (new donors only - required before donation)
 * 3. Donor Profile (view/edit - can skip to promise if just registered)
 * 4. Promise Creation (amount, frequency, campaign, notes)
 * 5. Payment Method Selection (saved methods or add new)
 * 6. New Payment Method (if adding new card/bank)
 * 7. Payment Preview & Confirmation
 * 8. Success Screen
 */

// Steps enum for cleaner code
const STEPS = {
  EMAIL_VERIFICATION: "email_verification",
  DONOR_REGISTRATION: "donor_registration",
  DONOR_PROFILE: "donor_profile",
  PROMISE_SELECTION: "promise_selection",
  EXISTING_PROMISE_PAYMENT: "existing_promise_payment",
  PROMISE_FORM: "promise_form",
  PAYMENT_METHOD_SELECTION: "payment_method_selection",
  NEW_PAYMENT_METHOD: "new_payment_method",
  PREVIEW: "preview",
  SUCCESS: "success",
};

const DesktopForm = ({ fundraiserData }) => {
  const { t } = useCustomTranslation("DonorFundraiser");
  const { campaign, organization, url: fundraiserToken } = fundraiserData;
  const requestedPromiseId = useMemo(
    () => new URLSearchParams(window.location.search).get("promise") || "",
    []
  );

  // ========== Storage Key for Persistence ==========
  const storageKey = `fundraiser_form_${fundraiserToken}${requestedPromiseId ? `_promise_${requestedPromiseId}` : ""}`;

  // ========== Load Saved State ==========
  const loadSavedState = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return null;

      const parsedState = JSON.parse(saved);
      const sessionExpiresAt = Number(parsedState.fundraiserSessionExpiresAt || 0);

      if ((parsedState.otpVerified && !parsedState.fundraiserSessionToken) || (sessionExpiresAt && sessionExpiresAt <= Date.now())) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return parsedState;
    } catch (error) {
      console.error("Error loading saved fundraiser state:", error);
      return null;
    }
  };

  // ========== Save State to Storage ==========
  const saveState = (state) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error("Error saving fundraiser state:", error);
    }
  };

  // ========== Clear Saved State ==========
  const clearSavedState = () => {
    try {
      const storagePrefix = `fundraiser_form_${fundraiserToken}`;
      Object.keys(localStorage)
        .filter((key) => key.startsWith(storagePrefix))
        .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Failed to clear fundraiser form state", error);
    }
  };

  // ========== Initialize State from Storage ==========
  const savedState = loadSavedState();

  // ========== State Initialization ==========
  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL_VERIFICATION);
  const [stepHistory, setStepHistory] = useState([STEPS.EMAIL_VERIFICATION]);

  // ========== Email & OTP State ==========
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const {
    fundraiserSessionToken,
    fundraiserSessionTokenRef,
    fundraiserSessionExpiresAt,
    setFundraiserSession,
    clearFundraiserSession,
    setExpiryHandler,
  } = useFundraiserSession();
  const [isNewDonor, setIsNewDonor] = useState(false);

  // ========== Donor State ==========
  const [donorData, setDonorData] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    address: "",
    zip_code: "",
  });
  const [savedPaymentMethods, setSavedPaymentMethods] = useState({
    cards: [],
    banks: [],
  });

  // ========== Promise State ==========
  const [promiseData, setPromiseData] = useState(null);
  const [existingPromises, setExistingPromises] = useState([]);
  const [selectedExistingPromise, setSelectedExistingPromise] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("one_time");
  const [frequency, setFrequency] = useState("monthly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [numberOfPayments, setNumberOfPayments] = useState(null);
  const [perPaymentAmount, setPerPaymentAmount] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(campaign?._id || "");
  const [notes, setNotes] = useState("");
  const [surchargeEnabled, setSurchargeEnabled] = useState(false);
  const [promiseOnly, setPromiseOnly] = useState(false);

  // ========== Payment Method State ==========
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [addingNewPaymentMethod, setAddingNewPaymentMethod] = useState(false);

  // ========== Card Tokenization State ==========
  const [cardTokenized, setCardTokenized] = useState(false);
  const [cardTokenData, setCardTokenData] = useState(null);
  const [paymentTokenId, setPaymentTokenId] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [cardLast4, setCardLast4] = useState("");
  const [cardBrand, setCardBrand] = useState("");

  // ========== ACH Tokenization State ==========
  const [achTokenized, setAchTokenized] = useState(false);
  const [bankName, setBankName] = useState("");
  const [bankLast4, setBankLast4] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");

  // ========== Transaction State ==========
  const [transactionId, setTransactionId] = useState(null);
  const [promiseId, setPromiseId] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // ========== Restore State from localStorage ==========
  useEffect(() => {
    if (savedState) {
      setCurrentStep(savedState.currentStep || STEPS.EMAIL_VERIFICATION);
      setStepHistory(savedState.stepHistory || [STEPS.EMAIL_VERIFICATION]);
      setEmail(savedState.email || "");
      setOtp(savedState.otp || "");
      setOtpSent(savedState.otpSent || false);
      setOtpVerified(savedState.otpVerified || false);
      setFundraiserSession(
        savedState.fundraiserSessionToken || "",
        savedState.fundraiserSessionExpiresAt || null
      );
      setIsNewDonor(savedState.isNewDonor || false);
      setDonorData(savedState.donorData || null);
      setFormData(savedState.formData || {
        first_name: "",
        last_name: "",
        phone: "",
        address: "",
        zip_code: "",
      });
      setSavedPaymentMethods(savedState.savedPaymentMethods || {
        cards: [],
        banks: [],
      });
      setPromiseData(savedState.promiseData || null);
      setExistingPromises(savedState.existingPromises || []);
      setSelectedExistingPromise(savedState.selectedExistingPromise || null);
      setAmount(savedState.amount || "");
      setPaymentType(savedState.paymentType || "one_time");
      setFrequency(savedState.frequency || "monthly");
      setStartDate(savedState.startDate || "");
      setEndDate(savedState.endDate || null);
      setNumberOfPayments(savedState.numberOfPayments || null);
      setPerPaymentAmount(savedState.perPaymentAmount || null);
      setSelectedCampaign(savedState.selectedCampaign || campaign?._id || "");
      setNotes(savedState.notes || "");
      setSurchargeEnabled(savedState.surchargeEnabled || false);
      setPromiseOnly(savedState.promiseOnly || false);
      setPaymentMethod(savedState.paymentMethod || "card");
      setSelectedPaymentMethodId(savedState.selectedPaymentMethodId || null);
      setAddingNewPaymentMethod(savedState.addingNewPaymentMethod || false);
      setCardTokenized(savedState.cardTokenized || false);
      setCardTokenData(savedState.cardTokenData || null);
      setPaymentTokenId(savedState.paymentTokenId || null);
      setPaymentMethodId(savedState.paymentMethodId || null);
      setCardLast4(savedState.cardLast4 || "");
      setCardBrand(savedState.cardBrand || "");
      setAchTokenized(savedState.achTokenized || false);
      setBankName(savedState.bankName || "");
      setBankLast4(savedState.bankLast4 || "");
      setAccountHolderName(savedState.accountHolderName || "");
      setTransactionId(savedState.transactionId || null);
      setPromiseId(savedState.promiseId || null);
      setSubmitError(savedState.submitError || null);
    }
  }, []); // Empty dependency array - only run once on mount

  // ========== SDK State ==========
  const cloverRef = useRef(null);
  const cloverElements = useRef({});
  const cloverMounted = useRef(false);
  const paymentIntentRef = useRef({ fingerprint: null, id: null });
  const promisesRequestVersionRef = useRef(0);
  const otpVerificationVersionRef = useRef(0);
  const [cloverReady, setCloverReady] = useState(false);
  const [cloverLoading, setCloverLoading] = useState(false);
  const [cloverError, setCloverError] = useState("");
  const [forteReady, setForteReady] = useState(false);
  const [forteLoading, setForteLoading] = useState(false);
  const [forteError, setForteError] = useState("");

  // ========== Payment Config ==========
  const [cardPaymentAvailable, setCardPaymentAvailable] = useState(true);
  // ACH/Forte is temporarily disabled in production.
  // Keep bank payment method unavailable until PROD Forte env is ready.
  const [achPaymentAvailable, setAchPaymentAvailable] = useState(false);

  // ========== Computed Values ==========
  const surchargeValue = useMemo(() => {
    return calculateSurcharge(
      surchargeEnabled,
      amount,
      paymentMethod,
      organization
    );
  }, [surchargeEnabled, amount, paymentMethod, organization]);

  const totalAmount = useMemo(() => {
    return calculateTotal(amount, surchargeEnabled, surchargeValue);
  }, [amount, surchargeEnabled, surchargeValue]);

  // ========== Save State to localStorage ==========
  useEffect(() => {
    const stateToSave = {
      currentStep,
      stepHistory,
      email,
      otp,
      otpSent,
      otpVerified,
      fundraiserSessionToken,
      fundraiserSessionExpiresAt,
      isNewDonor,
      donorData,
      formData,
      savedPaymentMethods,
      promiseData,
      existingPromises,
      selectedExistingPromise,
      amount,
      paymentType,
      frequency,
      startDate,
      endDate,
      numberOfPayments,
      perPaymentAmount,
      selectedCampaign,
      notes,
      surchargeEnabled,
      promiseOnly,
      paymentMethod,
      selectedPaymentMethodId,
      addingNewPaymentMethod,
      cardTokenized,
      cardTokenData,
      paymentTokenId,
      paymentMethodId,
      cardLast4,
      cardBrand,
      achTokenized,
      bankName,
      bankLast4,
      accountHolderName,
      transactionId,
      promiseId,
    };
    saveState(stateToSave);
  }, [
    currentStep,
    stepHistory,
    email,
    otp,
    otpSent,
    otpVerified,
    fundraiserSessionToken,
    fundraiserSessionExpiresAt,
    isNewDonor,
    donorData,
    formData,
    savedPaymentMethods,
    promiseData,
    existingPromises,
    selectedExistingPromise,
    amount,
    paymentType,
    frequency,
    startDate,
    endDate,
    numberOfPayments,
    perPaymentAmount,
    selectedCampaign,
    notes,
    surchargeEnabled,
    promiseOnly,
    paymentMethod,
    selectedPaymentMethodId,
    addingNewPaymentMethod,
    cardTokenized,
    cardTokenData,
    paymentTokenId,
    paymentMethodId,
    cardLast4,
    cardBrand,
    achTokenized,
    bankName,
    bankLast4,
    accountHolderName,
    transactionId,
    promiseId,
  ]);

  // ========== Clear State on Success ==========
  useEffect(() => {
    if (currentStep === STEPS.SUCCESS) {
      clearSavedState();
    }
  }, [currentStep]);

  // ========== API Mutations ==========
  const sendOtpMutation = useApiClientMutation(
    (body) => api_endpoints.sendFundraiserOTP(fundraiserToken, body),
    [],
    () => {
      setOtp("");
      setOtpSent(true);
    }
  );

  const applyVerifiedOtpResponse = (data) => {
    const promisesRequestVersion = ++promisesRequestVersionRef.current;
    setOtpVerified(true);
    setFundraiserSession(data.sessionToken, data.sessionExpiresAt);
    setIsNewDonor(data.isNewDonor);
    if (!data.isNewDonor && data.donor) {
      setDonorData(data.donor);
      setFormData({
        first_name: data.donor.first_name || "",
        last_name: data.donor.last_name || "",
        phone: data.donor.phone || "",
        address: data.donor.adresse || "",
        zip_code: data.donor.zip_code || "",
      });
      // Load saved payment methods
      if (data.donor.cards) setSavedPaymentMethods(prev => ({ ...prev, cards: data.donor.cards }));
      if (data.donor.banks) setSavedPaymentMethods(prev => ({ ...prev, banks: data.donor.banks }));
      setExistingPromises(Array.isArray(data.promises) ? data.promises : []);
      api_endpoints
        .getFundraiserPromises(fundraiserToken, data.sessionToken)
        .then((response) => {
          if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
          setExistingPromises(Array.isArray(response.data) ? response.data : []);
        })
        .catch((error) => {
          if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
          console.error("Failed to load existing promises:", error);
        });
    } else {
      setExistingPromises([]);
    }
  };

  const verifyOtpMutation = useApiClientMutation(
    (body) => api_endpoints.verifyFundraiserOTP(fundraiserToken, body),
    []
  );

  const createDonorMutation = useApiClientMutation(
    (body) => api_endpoints.createFundraiserDonor(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setDonorData(data.donor);
      setIsNewDonor(false); // No longer new after registration
      if (Array.isArray(data?.donor?.cards)) {
        setSavedPaymentMethods((prev) => ({ ...prev, cards: data.donor.cards }));
      }
      if (Array.isArray(data?.donor?.banks)) {
        setSavedPaymentMethods((prev) => ({ ...prev, banks: data.donor.banks }));
      }
    }
  );

  const updateDonorMutation = useApiClientMutation(
    (body) => api_endpoints.updateFundraiserDonor(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setDonorData(data.donor);
    }
  );

  const createPromiseMutation = useApiClientMutation(
    (body) => api_endpoints.createFundraiserPromise(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setPromiseData(data.promise);
      setPromiseId(data.promise?._id);
    }
  );

  const tokenizeCardMutation = useApiClientMutation(
    (body) => api_endpoints.tokenizeFundraiserCard(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setCardTokenized(true);
      setPaymentTokenId(data.paymentTokenId);
      setPaymentMethodId(data.donorCardId);
      setCardLast4(data.last4);
      setCardBrand(data.brand);
      setSavedPaymentMethods((prev) => {
        const existingCards = Array.isArray(prev?.cards) ? prev.cards : [];
        const updatedCard = {
          _id: data.donorCardId,
          brand: data.brand || "Card",
          lastNumbers: data.last4 || "",
          last4: data.last4 || "",
          cardholderName: `${donorData?.first_name || ""} ${donorData?.last_name || ""}`.trim(),
          expiration: "",
        };

        const alreadyExists = existingCards.some((c) => String(c._id) === String(data.donorCardId));
        return {
          ...prev,
          cards: alreadyExists
            ? existingCards.map((c) => (String(c._id) === String(data.donorCardId) ? { ...c, ...updatedCard } : c))
            : [updatedCard, ...existingCards],
        };
      });
    }
  );

  const tokenizeAchMutation = useApiClientMutation(
    (body) => api_endpoints.tokenizeFundraiserACH(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setAchTokenized(true);
      setPaymentTokenId(data.paymentTokenId);
      setPaymentMethodId(data.donorBankId);
      setBankLast4(data.last4);
    }
  );

  const chargeMutation = useApiClientMutation(
    (body) => api_endpoints.processFundraiserCharge(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setTransactionId(data.transactionId);
      navigateTo(STEPS.SUCCESS);
    }
  );

  const subscribeMutation = useApiClientMutation(
    (body) => api_endpoints.createFundraiserSubscription(fundraiserToken, body, fundraiserSessionTokenRef.current),
    [],
    (data) => {
      setTransactionId(data.subscriptionId);
      navigateTo(STEPS.SUCCESS);
    }
  );

  // ========== Navigation Helpers ==========
  const navigateTo = useCallback((step) => {
    setStepHistory(prev => [...prev, step]);
    setCurrentStep(step);
  }, []);

  const resetFundraiserFlow = useCallback(
    ({ clearEmail = false } = {}) => {
      promisesRequestVersionRef.current += 1;
      otpVerificationVersionRef.current += 1;
      paymentIntentRef.current = { fingerprint: null, id: null };
      clearSavedState();
      sendOtpMutation.reset();
      verifyOtpMutation.reset();

      setCurrentStep(STEPS.EMAIL_VERIFICATION);
      setStepHistory([STEPS.EMAIL_VERIFICATION]);

      if (clearEmail) setEmail("");
      setOtp("");
      setOtpSent(false);
      setOtpVerified(false);
      clearFundraiserSession();
      setIsNewDonor(false);

      setDonorData(null);
      setFormData({
        first_name: "",
        last_name: "",
        phone: "",
        address: "",
        zip_code: "",
      });
      setSavedPaymentMethods({ cards: [], banks: [] });

      setPromiseData(null);
      setExistingPromises([]);
      setSelectedExistingPromise(null);
      setAmount("");
      setPaymentType("one_time");
      setFrequency("monthly");
      setStartDate("");
      setEndDate(null);
      setNumberOfPayments(null);
      setPerPaymentAmount(null);
      setSelectedCampaign(campaign?._id || "");
      setNotes("");
      setSurchargeEnabled(false);
      setPromiseOnly(false);

      setPaymentMethod("card");
      setSelectedPaymentMethodId(null);
      setAddingNewPaymentMethod(false);

      setCardTokenized(false);
      setCardTokenData(null);
      setPaymentTokenId(null);
      setPaymentMethodId(null);
      setCardLast4("");
      setCardBrand("");

      setAchTokenized(false);
      setBankName("");
      setBankLast4("");
      setAccountHolderName("");

      setTransactionId(null);
      setPromiseId(null);
      setSubmitError(null);

      setCloverReady(false);
      setCloverError("");
      setForteReady(false);
      setForteError("");
      cloverMounted.current = false;
    },
    [campaign?._id, clearFundraiserSession, sendOtpMutation, verifyOtpMutation]
  );

  const resetToEmailStep = useCallback(() => {
    resetFundraiserFlow();
  }, [resetFundraiserFlow]);

  const navigateBack = useCallback(() => {
    // Once verification has moved into registration/profile, going back should
    // restart verification instead of returning to an already-verified OTP step
    // that immediately auto-forwards again.
    if (
      currentStep === STEPS.DONOR_REGISTRATION ||
      currentStep === STEPS.DONOR_PROFILE
    ) {
      resetToEmailStep();
      return;
    }

    if (stepHistory.length > 1) {
      const newHistory = [...stepHistory];
      newHistory.pop();
      setStepHistory(newHistory);
      setCurrentStep(newHistory[newHistory.length - 1]);
    }
  }, [currentStep, resetToEmailStep, stepHistory]);


  // ========== Get Steps for Progress Indicator ==========
  const getDisplaySteps = useMemo(() => {
    const steps = [
      { key: STEPS.EMAIL_VERIFICATION, label: t("verification", "Verification") },
    ];

    if (isNewDonor) {
      steps.push({ key: STEPS.DONOR_REGISTRATION, label: t("registration", "Registration") });
    } else {
      steps.push({ key: STEPS.DONOR_PROFILE, label: t("profile", "Profile") });
    }

    if (!isNewDonor && (existingPromises.length > 0 || currentStep === STEPS.PROMISE_SELECTION)) {
      steps.push({ key: STEPS.PROMISE_SELECTION, label: t("promises", "Promises") });
    }

    steps.push(
      selectedExistingPromise
        ? { key: STEPS.EXISTING_PROMISE_PAYMENT, label: t("promise", "Promise") }
        : { key: STEPS.PROMISE_FORM, label: t("donation", "Donation") }
    );

    if (!promiseOnly) {
      steps.push(
        { key: STEPS.PAYMENT_METHOD_SELECTION, label: t("payment", "Payment") },
        { key: STEPS.PREVIEW, label: t("confirm", "Confirm") },
      );
    }

    return steps;
  }, [isNewDonor, existingPromises.length, selectedExistingPromise, currentStep, promiseOnly, t]);

  const currentStepIndex = useMemo(() => {
    return getDisplaySteps.findIndex(s => s.key === currentStep);
  }, [currentStep, getDisplaySteps]);

  // ========== Initialize Clover SDK ==========
  useEffect(() => {
    if (currentStep !== STEPS.NEW_PAYMENT_METHOD || paymentMethod !== "card") return;

    let cancelled = false;

    const initClover = async () => {
      try {
        setCloverLoading(true);
        setCloverError("");
        await loadScript(cloverSdkUrl());
        if (cancelled) return;
        if (!window.Clover) throw new Error("missing-clover");

        const { data } = await api_endpoints.getFundraiserCloverPakms(fundraiserToken, organization._id, fundraiserSessionTokenRef.current);
        if (cancelled) return;
        const apiAccessKey = data?.apiAccessKey;
        if (!apiAccessKey) throw new Error("missing-key");

        const clover = new window.Clover(apiAccessKey);
        if (cancelled) return;
        cloverRef.current = clover;

        const elements = clover.elements();
        const styles = {
          body: { fontFamily: "Inter, system-ui, sans-serif", fontSize: "18px" },
          input: { fontSize: "18px", color: "#1f2937" },
        };
        cloverElements.current = {
          number: elements.create("CARD_NUMBER", styles),
          date: elements.create("CARD_DATE", styles),
          cvv: elements.create("CARD_CVV", styles),
          postal: elements.create("CARD_POSTAL_CODE", styles),
        };

        setCloverReady(true);
      } catch (error) {
        if (cancelled) return;
        console.error("[DesktopForm] Clover init", error);
        const errorMessage = translateFundraiserError(t, error?.response?.data?.message);
        if (errorMessage === "Organization not configured for card payments") {
          setCardPaymentAvailable(false);
          setCloverError(t("card_not_configured", "Card payments are not available. Please use bank transfer."));
          setPaymentMethod("ach");
        } else {
          setCloverError(t("card_setup_error", "Failed to initialize card form. Please try again."));
        }
      } finally {
        if (!cancelled) setCloverLoading(false);
      }
    };

    initClover();
    return () => { cancelled = true; };
  }, [currentStep, paymentMethod, fundraiserToken, organization._id, fundraiserSessionToken, t]);

  // ========== Mount Clover Elements ==========
  useEffect(() => {
    if (!cloverReady || paymentMethod !== "card" || currentStep !== STEPS.NEW_PAYMENT_METHOD) return;
    const { number, date, cvv, postal } = cloverElements.current || {};
    if (!number || !date || !cvv || !postal) return;
    if (cloverMounted.current) return;

    const waitForElements = setInterval(() => {
      const numberEl = document.querySelector("#clover-card-number");
      const dateEl = document.querySelector("#clover-card-date");
      const cvvEl = document.querySelector("#clover-card-cvv");
      const postalEl = document.querySelector("#clover-card-postal");

      if (numberEl && dateEl && cvvEl && postalEl) {
        clearInterval(waitForElements);
        try {
          number.mount("#clover-card-number");
          date.mount("#clover-card-date");
          cvv.mount("#clover-card-cvv");
          postal.mount("#clover-card-postal");
          cloverMounted.current = true;
        } catch (e) {
          console.error("[DesktopForm] Clover mount", e);
          setCloverError(t("card_mount_error", "Failed to display card form"));
        }
      }
    }, 100);

    return () => clearInterval(waitForElements);
  }, [cloverReady, paymentMethod, currentStep, t]);

  // ========== Initialize Forte SDK ==========
  useEffect(() => {
    if (currentStep !== STEPS.NEW_PAYMENT_METHOD || paymentMethod !== "ach") return;

    let cancelled = false;

    (async () => {
      try {
        setForteLoading(true);
        setForteError("");
        await loadScript(forteJsUrl());
        if (cancelled) return;
        const ok = typeof window.Forte !== "undefined" || typeof window.forte !== "undefined";
        if (!ok) throw new Error("missing-forte");
        setForteReady(true);
      } catch (error) {
        if (cancelled) return;
        console.error("[DesktopForm] Forte init", error);
        setForteError(t("ach_setup_error", "Failed to initialize bank form"));
      } finally {
        if (!cancelled) setForteLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentStep, paymentMethod, t]);

  // ========== Forte Callbacks ==========
  const handleForteSuccess = useCallback(async (resp) => {
    try {
      tokenizeAchMutation.mutate({
        email,
        donorId: donorData?._id,
        forteToken: resp?.one_time_token || resp?.onetime_token,
        bankInfo: {
          bankName,
          firstName: accountHolderName.split(" ")[0],
          lastName: accountHolderName.split(" ").slice(1).join(" "),
          last4: resp?.last_4,
        },
      });
    } catch (error) {
      setForteError(error.message || t("ach_tokenization_failed", "Failed to save bank account"));
    }
  }, [email, donorData, bankName, accountHolderName, tokenizeAchMutation, t]);

  const handleForteError = useCallback((resp) => {
    setForteError(resp?.response_description || t("ach_tokenization_failed", "Bank verification failed"));
  }, [t]);

  // Set up Forte callbacks
  useEffect(() => {
    if (paymentMethod !== "ach" || currentStep !== STEPS.NEW_PAYMENT_METHOD) return;
    window.__forteSuccess = handleForteSuccess;
    window.__forteError = handleForteError;
    return () => {
      delete window.__forteSuccess;
      delete window.__forteError;
    };
  }, [paymentMethod, currentStep, handleForteSuccess, handleForteError]);

  // ========== Event Handlers ==========
  const handleSendOtp = () => {
    const normalizedEmail = normalizeDonorEmail(email);
    if (!EMAIL_REGEX.test(normalizedEmail)) return;

    setEmail(normalizedEmail);
    setOtp("");
    setOtpVerified(false);
    sendOtpMutation.mutate({ email: normalizedEmail });
  };

  const handleVerifyOtp = () => {
    const normalizedEmail = normalizeDonorEmail(email);
    const normalizedOtp = otp.trim();
    if (!EMAIL_REGEX.test(normalizedEmail) || !normalizedOtp) return;

    setEmail(normalizedEmail);
    const verificationVersion = ++otpVerificationVersionRef.current;
    verifyOtpMutation.mutate(
      { email: normalizedEmail, otp: normalizedOtp },
      {
        onSuccess: (data) => {
          if (verificationVersion !== otpVerificationVersionRef.current) return;
          applyVerifiedOtpResponse(data);
        },
      }
    );
  };

  const handleChangeEmail = () => {
    resetToEmailStep();
  };

  const handleOtpVerified = () => {
    if (isNewDonor) {
      navigateTo(STEPS.DONOR_REGISTRATION);
    } else {
      navigateTo(STEPS.DONOR_PROFILE);
    }
  };

  const handleRegistration = async (registrationData) => {
    try {
      await createDonorMutation.mutateAsync({
        email,
        ...registrationData,
      });
      // Registration is complete. Keep Profile as the meaningful previous step so
      // Back from the promise form never returns to a registration form that was
      // already submitted.
      setStepHistory([
        STEPS.EMAIL_VERIFICATION,
        STEPS.DONOR_PROFILE,
        STEPS.PROMISE_FORM,
      ]);
      setCurrentStep(STEPS.PROMISE_FORM);
    } catch (error) {
      console.error("Registration failed:", error);
    }
  };

  const handleProfileUpdate = async (profileData) => {
    try {
      await updateDonorMutation.mutateAsync({
        donorId: donorData?._id,
        ...profileData,
      });
    } catch (error) {
      console.error("Profile update failed:", error);
    }
  };

  const handleExistingPromiseSelect = (promise) => {
    setSelectedExistingPromise(promise);
    setPromiseData(promise);
    setPromiseId(promise._id);
    setPromiseOnly(false);
    navigateTo(STEPS.EXISTING_PROMISE_PAYMENT);
  };

  const handleCreateNewPromise = () => {
    setSelectedExistingPromise(null);
    setPromiseData(null);
    setPromiseId(null);
    setAmount("");
    setPaymentType("one_time");
    setFrequency("monthly");
    setStartDate("");
    setEndDate(null);
    setNumberOfPayments(null);
    setPerPaymentAmount(null);
    setSurchargeEnabled(false);
    setPromiseOnly(false);
    navigateTo(STEPS.PROMISE_FORM);
  };

  const handleProfileContinue = () => {
    const requestedPromise = existingPromises.find(
      (promise) => String(promise._id) === String(requestedPromiseId)
    );
    if (requestedPromise) {
      handleExistingPromiseSelect(requestedPromise);
      return;
    }
    if (existingPromises.length > 0) {
      navigateTo(STEPS.PROMISE_SELECTION);
      return;
    }
    navigateTo(STEPS.PROMISE_FORM);
  };

  const handleExistingPromiseContinue = (paymentSetup) => {
    setAmount(paymentSetup.amount);
    setPaymentType(paymentSetup.paymentType);
    setFrequency(paymentSetup.frequency);
    setStartDate(paymentSetup.startDate);
    setEndDate(paymentSetup.endDate || null);
    setNumberOfPayments(paymentSetup.numberOfPayments || null);
    setPerPaymentAmount(paymentSetup.perPaymentAmount || null);
    setSurchargeEnabled(false);
    setPromiseOnly(false);
    navigateTo(STEPS.PAYMENT_METHOD_SELECTION);
  };

  const handlePromiseSubmit = async (promiseFormData) => {
    try {
      setSelectedExistingPromise(null);
      // Create the promise
      await createPromiseMutation.mutateAsync({
        donorId: donorData?._id,
        campaignId: promiseFormData.campaignId || selectedCampaign,
        amount: promiseFormData.amount,
        paymentType: promiseFormData.paymentType,
        frequency: promiseFormData.frequency,
        startDate: promiseFormData.startDate,
        endDate: promiseFormData.endDate || null,
        numberOfPayments: promiseFormData.numberOfPayments || null,
        notes: promiseFormData.notes,
        surchargeEnabled: promiseFormData.surchargeEnabled,
      });

      // Update local state
      setAmount(promiseFormData.amount);
      setPaymentType(promiseFormData.paymentType);
      setFrequency(promiseFormData.frequency);
      setStartDate(promiseFormData.startDate);
      setEndDate(promiseFormData.endDate || null);
      setNumberOfPayments(promiseFormData.numberOfPayments || null);
      setPerPaymentAmount(promiseFormData.perPaymentAmount || null);
      setNotes(promiseFormData.notes);
      setSurchargeEnabled(promiseFormData.surchargeEnabled);

      if (promiseFormData.promiseOnly) {
        // Promise without payment - go to success
        setPromiseOnly(true);
        navigateTo(STEPS.SUCCESS);
      } else {
        // Continue to payment
        navigateTo(STEPS.PAYMENT_METHOD_SELECTION);
      }
    } catch (error) {
      console.error("Promise creation failed:", error);
    }
  };

  const handlePaymentMethodSelection = (method) => {
    // ACH/bank selection is disabled for now (no PROD Forte environment).
    if (method?.type === "ach" || method?.type === "bank") {
      setSubmitError(t("ach_temporarily_unavailable", "Bank payment is temporarily unavailable. Please use card."));
      return;
    }

    if (method.isNew) {
      setAddingNewPaymentMethod(true);
      setPaymentMethod(method.type);
      navigateTo(STEPS.NEW_PAYMENT_METHOD);
    } else {
      setSelectedPaymentMethodId(method.id);
      setPaymentMethodId(method.id);
      setPaymentMethod(method.type);
      if (method.type === "card") {
        setCardLast4(method.last4);
        setCardBrand(method.brand);
      } else {
        setBankLast4(method.last4);
        setBankName(method.bankName);
      }
      navigateTo(STEPS.PREVIEW);
    }
  };

  const handleTokenizeCard = async () => {
    if (!cloverReady || !cloverRef.current) {
      setCloverError(t("card_not_ready", "Card form not ready"));
      return;
    }

    try {
      const result = await cloverRef.current.createToken();
      if (result?.errors) {
        const combined = Object.values(result.errors).join(" ");
        throw new Error(combined || "card_tokenization_failed");
      }

      const tokenValue = result?.token;
      if (!tokenValue) {
        setCloverError(t("missing_card_token", "Could not create card token"));
        return;
      }

      tokenizeCardMutation.mutate({
        email,
        donorId: donorData?._id,
        cloverToken: tokenValue,
        cardInfo: {
          brand: result?.card?.brand,
          first6: result?.card?.first6 || result?.card?.first6Digits || result?.card?.bin || "",
          last4: result?.card?.last4,
          exp_month: result?.card?.expMonth || result?.card?.exp_month || result?.card?.month || "",
          exp_year: result?.card?.expYear || result?.card?.exp_year || result?.card?.year || "",
          cardholderName:
            result?.card?.name ||
            result?.card?.cardholderName ||
            `${donorData?.first_name || ""} ${donorData?.last_name || ""}`.trim(),
        },
      });
    } catch (error) {
      setCloverError(error.message || t("card_tokenization_failed", "Failed to tokenize card"));
    }
  };

  const handleCardTokenized = () => {
    navigateTo(STEPS.PREVIEW);
  };

  const handleAchTokenized = () => {
    // ACH flow intentionally disabled.
    navigateTo(STEPS.PREVIEW);
  };

  const handlePaymentConfirm = async () => {
    setSubmitError(null);

    if (!promiseId) {
      setSubmitError(t("promise_missing", "Please create a promise before submitting payment."));
      return;
    }

    const paymentIntentFingerprint = JSON.stringify([
      promiseId,
      Number(amount),
      surchargeEnabled ? Number(surchargeValue || 0) : 0,
      paymentMethod,
      paymentMethodId,
    ]);
    if (
      paymentType === "one_time" &&
      paymentIntentRef.current.fingerprint !== paymentIntentFingerprint
    ) {
      paymentIntentRef.current = {
        fingerprint: paymentIntentFingerprint,
        id: createPaymentIntentId(),
      };
    }

    const payload = buildPaymentPayload({
      email,
      donorData,
      formData,
      amount,
      surchargeEnabled,
      surchargeValue,
      paymentMethod,
      paymentMethodId,
      paymentType,
      frequency,
      startDate,
      endDate,
      numberOfPayments,
      perPaymentAmount,
      promiseId,
      paymentIntentId:
        paymentType === "one_time" ? paymentIntentRef.current.id : null,
    });

    try {
      if (paymentType === "one_time") {
        await chargeMutation.mutateAsync(payload);
      } else {
        await subscribeMutation.mutateAsync(payload);
      }
    } catch (error) {
      setSubmitError(
        translateFundraiserError(t, error?.response?.data?.message) || 
        t("payment_failed", "Payment failed. Please try again.")
      );
    }
  };

  const handleNewDonation = async () => {
    paymentIntentRef.current = { fingerprint: null, id: null };
    const hadExistingPromise = Boolean(selectedExistingPromise);
    const wasPromiseOnly = promiseOnly;
    const shouldReturnToPromises = hadExistingPromise || wasPromiseOnly;
    const hadLoadedPromises = existingPromises.length > 0;
    const promisesRequestVersion = ++promisesRequestVersionRef.current;

    setAmount("");
    setPaymentType("one_time");
    setFrequency("monthly");
    setStartDate("");
    setEndDate(null);
    setNumberOfPayments(null);
    setPerPaymentAmount(null);
    setNotes("");
    setSurchargeEnabled(false);
    setPromiseOnly(false);
    setPromiseData(null);
    setPromiseId(null);
    setSelectedExistingPromise(null);
    setSelectedPaymentMethodId(null);
    setPaymentMethodId(null);
    setCardTokenized(false);
    setAchTokenized(false);
    setTransactionId(null);
    setSubmitError(null);
    cloverMounted.current = false;

    try {
      const response = await api_endpoints.getFundraiserPromises(
        fundraiserToken,
        fundraiserSessionTokenRef.current
      );
      if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
      const refreshedPromises = Array.isArray(response.data) ? response.data : [];
      setExistingPromises(refreshedPromises);
      if (shouldReturnToPromises || refreshedPromises.length > 0) {
        setStepHistory([STEPS.PROMISE_SELECTION]);
        setCurrentStep(STEPS.PROMISE_SELECTION);
        return;
      }
    } catch (error) {
      if (promisesRequestVersion !== promisesRequestVersionRef.current) return;
      console.error("Failed to refresh promises:", error);
      if (requestedPromiseId || shouldReturnToPromises || hadLoadedPromises) {
        setSubmitError(
          t(
            "failed_to_refresh_promises",
            "We could not refresh your promises. Please try again."
          )
        );
        setStepHistory([STEPS.PROMISE_SELECTION]);
        setCurrentStep(STEPS.PROMISE_SELECTION);
        return;
      }
    }

    setStepHistory([STEPS.PROMISE_FORM]);
    setCurrentStep(STEPS.PROMISE_FORM);
  };

  const handleLogout = useCallback(() => {
    resetFundraiserFlow({ clearEmail: true });
  }, [resetFundraiserFlow]);

  useEffect(() => {
    setExpiryHandler(handleLogout);
    return () => setExpiryHandler(null);
  }, [handleLogout, setExpiryHandler]);

  useEffect(() => {
    const protectedMutationErrors = [
      createDonorMutation.error,
      updateDonorMutation.error,
      createPromiseMutation.error,
      tokenizeCardMutation.error,
      tokenizeAchMutation.error,
      chargeMutation.error,
      subscribeMutation.error,
    ];

    if (protectedMutationErrors.some((error) => error?.response?.status === 401)) {
      handleLogout();
    }
  }, [
    createDonorMutation.error,
    updateDonorMutation.error,
    createPromiseMutation.error,
    tokenizeCardMutation.error,
    tokenizeAchMutation.error,
    chargeMutation.error,
    subscribeMutation.error,
    handleLogout,
  ]);

  // ========== Render Step Content ==========
  const renderStepContent = () => {
    switch (currentStep) {
      case STEPS.EMAIL_VERIFICATION:
        return (
          <EmailVerification
            t={t}
            email={email}
            setEmail={setEmail}
            otp={otp}
            setOtp={setOtp}
            otpSent={otpSent}
            otpVerified={otpVerified}
            isNewDonor={isNewDonor}
            donorData={donorData}
            onSendOtp={handleSendOtp}
            onVerifyOtp={handleVerifyOtp}
            onChangeEmail={handleChangeEmail}
            onContinue={handleOtpVerified}
            sendOtpLoading={sendOtpMutation.isPending}
            verifyOtpLoading={verifyOtpMutation.isPending}
            sendOtpError={translateFundraiserError(t, sendOtpMutation.error?.response?.data?.message)}
            verifyOtpError={translateFundraiserError(t, verifyOtpMutation.error?.response?.data?.message)}
          />
        );

      case STEPS.DONOR_REGISTRATION:
        return (
          <DonorRegistration
            t={t}
            email={email}
            initialData={formData}
            onSubmit={handleRegistration}
            isLoading={createDonorMutation.isPending}
            error={translateFundraiserError(t, createDonorMutation.error?.response?.data?.message)}
          />
        );

      case STEPS.DONOR_PROFILE:
        return (
          <DonorProfile
            t={t}
            donorData={donorData}
            onUpdate={handleProfileUpdate}
            onContinue={handleProfileContinue}
            isLoading={updateDonorMutation.isPending}
            error={translateFundraiserError(t, updateDonorMutation.error?.response?.data?.message)}
          />
        );

      case STEPS.PROMISE_SELECTION:
        return (
          <ExistingPromises
            t={t}
            promises={existingPromises}
            requestedPromiseId={requestedPromiseId}
            onSelect={handleExistingPromiseSelect}
            onCreateNew={handleCreateNewPromise}
            error={submitError}
          />
        );

      case STEPS.EXISTING_PROMISE_PAYMENT:
        return (
          <ExistingPromisePayment
            t={t}
            promise={selectedExistingPromise}
            onContinue={handleExistingPromiseContinue}
          />
        );

      case STEPS.PROMISE_FORM:
        return (
          <PromiseForm
            t={t}
            organization={organization}
            campaigns={campaign ? [campaign] : []}
            defaultCampaignId={campaign._id}
            initialData={{
              amount,
              paymentType,
              frequency,
              startDate,
              notes,
              surchargeEnabled,
            }}
            onSubmit={handlePromiseSubmit}
            isLoading={createPromiseMutation.isPending}
            error={translateFundraiserError(t, createPromiseMutation.error?.response?.data?.message)}
            paymentMethod={paymentMethod}
            acceptRecurring={Boolean(campaign?.is_fundraiser_accept_recurring)}
          />
        );

      case STEPS.PAYMENT_METHOD_SELECTION:
        return (
          <PaymentMethodsList
            t={t}
            cards={savedPaymentMethods.cards}
            banks={savedPaymentMethods.banks}
            onSelect={handlePaymentMethodSelection}
            cardPaymentAvailable={cardPaymentAvailable}
            achPaymentAvailable={achPaymentAvailable}
          />
        );

      case STEPS.NEW_PAYMENT_METHOD:
        // ACH/bank payment method creation is disabled; keep card form only.
        if (paymentMethod === "card") {
          return (
            <CardPaymentForm
              t={t}
              cloverReady={cloverReady}
              cloverLoading={cloverLoading}
              cloverError={cloverError}
              cardTokenized={cardTokenized}
              cardLast4={cardLast4}
              cardBrand={cardBrand}
              onTokenize={handleTokenizeCard}
              onTokenized={handleCardTokenized}
              tokenizeLoading={tokenizeCardMutation.isPending}
              tokenizeError={translateFundraiserError(t, tokenizeCardMutation.error?.response?.data?.message)}
            />
          );
        }
        return (
          <Alert severity="warning">
            {t("ach_temporarily_unavailable", "Bank payment is temporarily unavailable. Please use card.")}
          </Alert>
        );

      case STEPS.PREVIEW:
        return (
          <PaymentPreview
            t={t}
            donorData={donorData || formData}
            amount={amount}
            surchargeEnabled={surchargeEnabled}
            surchargeValue={surchargeValue}
            totalAmount={totalAmount}
            paymentType={paymentType}
            frequency={frequency}
            startDate={startDate}
            paymentMethod={paymentMethod}
            cardLast4={cardLast4}
            cardBrand={cardBrand}
            bankName={bankName}
            bankLast4={bankLast4}
            onConfirm={handlePaymentConfirm}
            onEdit={navigateBack}
            isSubmitting={chargeMutation.isPending || subscribeMutation.isPending}
            submitError={submitError}
          />
        );

      case STEPS.SUCCESS:
        return (
          <DonationSuccess
            t={t}
            donorData={donorData || formData}
            amount={amount}
            totalAmount={totalAmount}
            paymentType={paymentType}
            frequency={frequency}
            transactionId={transactionId}
            promiseId={promiseId}
            onNewDonation={handleNewDonation}
            onClose={handleLogout}
            returnToPromises={Boolean(selectedExistingPromise) || promiseOnly}
          />
        );

      default:
        return null;
    }
  };

  // ========== Error State ==========
  if (!campaign || !organization) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh", p: 4 }}>
        <Alert severity="error" sx={{ fontSize: "1.1rem", py: 2 }}>
          {t("fundraiser_load_error", "Could not load fundraiser details. Please try again.")}
        </Alert>
      </Box>
    );
  }

  // ========== Main Render ==========
  return (
    <Box sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 5, borderRadius: 4, maxWidth: 900, mx: "auto" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <LanguageSwitcher />
          {otpVerified && (
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={handleLogout}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {t("logout", "Logout")}
            </Button>
          )}
        </Box>
        
        {/* Organization Logo & Header */}
        {organization?.logo && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <img
              src={organization.logo.startsWith("http") ? organization.logo : `${process.env.REACT_APP_S3_URL}${organization.logo}`}
              alt="Organization Logo"
              style={{ maxWidth: 140, maxHeight: 140, objectFit: "contain" }}
            />
          </Box>
        )}

        <Typography variant="h5" color="text.secondary" align="center" sx={{ mb: 1 }}>
          {organization?.legal_name}
        </Typography>

        <Typography
          variant="h4"
          gutterBottom
          align="center"
          sx={{
            fontWeight: 700,
            mb: 4,
            color: "primary.main",
            maxWidth: "100%",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {campaign?.name || t("campaign_name_unavailable", "Campaign")}
        </Typography>

        {/* Progress Indicator - Only show if not on success */}
        {currentStep !== STEPS.SUCCESS && (
          <Box sx={{ mb: 5 }}>
            <LinearProgress
              variant="determinate"
              value={currentStepIndex >= 0 ? ((currentStepIndex + 1) / getDisplaySteps.length) * 100 : 0}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "grey.200",
                "& .MuiLinearProgress-bar": { borderRadius: 5 },
              }}
            />
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 2, fontSize: "1rem" }}>
              {currentStepIndex >= 0 
                ? t("step_of", `Step ${currentStepIndex + 1} of ${getDisplaySteps.length}`)
                : ""
              }
            </Typography>
          </Box>
        )}

        {/* Step Content */}
        <Box sx={{ minHeight: 400, mb: 5 }}>
          {renderStepContent()}
        </Box>

        {/* Navigation Buttons - Only show if not on success and verification is complete */}
        {currentStep !== STEPS.SUCCESS && currentStep !== STEPS.EMAIL_VERIFICATION && (
          <Stack direction="row" spacing={3} justifyContent="center" sx={{ maxWidth: 500, mx: "auto" }}>
            <FriendlyButton
              variant="outlined"
              onClick={navigateBack}
              disabled={stepHistory.length <= 1}
              sx={{ flex: 1, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
            >
              <ArrowBackIcon sx={{ mr: 1 }} />
              {t("back", "Back")}
            </FriendlyButton>
          </Stack>
        )}

        {/* Powered by Footer */}
        <Box sx={{ mt: 6, pt: 4, borderTop: "1px solid", borderColor: "grey.200" }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
            Powered by
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img
              src={`${process.env.PUBLIC_URL}/Kiosk-Donations.svg`}
              alt="Kiosk Donations"
              style={{ width: 180, objectFit: "contain" }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default DesktopForm;
